import React, { useEffect, useState, useCallback } from 'react';
import { Paperclip, X, Loader } from 'react-feather';
import { DocumentService } from '../services/supabase.service';
import { translations } from '../../../utils/translations';
import { useStore } from '../../../store/useStore';

interface ActiveFile {
    filename: string;
    uploadedAt: string;
}

interface ActiveFilesProps {
    chatId: string | null;
    onFileDelete: (filename: string) => Promise<void>;
}

export const ActiveFiles: React.FC<ActiveFilesProps> = ({ chatId, onFileDelete }) => {
    const [files, setFiles] = useState<ActiveFile[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [deletingFiles, setDeletingFiles] = useState<string[]>([]);
    const documentService = DocumentService.getInstance();
    const { language, user } = useStore();

    const loadFiles = useCallback(async () => {
        if (!chatId || !user) return;
        
        setIsLoading(true);
        try {
            const uploadedFiles = await documentService.getUploadedFiles(chatId, user.uid);
            console.log('Loaded files:', {
                raw: uploadedFiles,
                count: uploadedFiles.length,
                filenames: uploadedFiles.map((f: ActiveFile) => f.filename),
                timestamps: uploadedFiles.map((f: ActiveFile) => f.uploadedAt)
            });
            setFiles(uploadedFiles);
        } catch (error) {
            console.error('Error loading files:', error);
        } finally {
            setIsLoading(false);
        }
    }, [chatId, documentService, user]);

    const handleDelete = async (filename: string) => {
        if (!user) {
            console.error('No user found');
            return;
        }

        setDeletingFiles(prev => [...prev, filename]);
        try {
            await onFileDelete(filename);
            // Clear files immediately to update UI
            setFiles([]);
            // Then reload files
            await loadFiles();
        } catch (error) {
            console.error('Error deleting file:', error);
            await loadFiles(); // Reload files in case of error
        } finally {
            setDeletingFiles(prev => prev.filter(f => f !== filename));
        }
    };

    // Load files when component mounts or chatId changes
    useEffect(() => {
        loadFiles();
    }, [loadFiles, chatId]);

    // Load files when receiving a file operation event
    useEffect(() => {
        const handleFileOperation = () => {
            console.log('File operation detected, reloading files...');
            loadFiles();
        };

        window.addEventListener('fileOperation', handleFileOperation);
        return () => {
            window.removeEventListener('fileOperation', handleFileOperation);
        };
    }, [loadFiles]);

    if (!chatId) return null;

    if (isLoading) {
        return (
            <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <Paperclip size={14} />
                    <span>{translations[language].activeFiles}</span>
                    <Loader size={14} className="animate-spin ml-2" />
                </div>
            </div>
        );
    }

    if (!files || files.length === 0) {
        return null;
    }

    return (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                <Paperclip size={14} />
                <span>{translations[language].activeFiles}</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
                {files.map((file) => (
                    <div
                        key={file.filename}
                        className="flex items-center gap-2 px-2 py-1 bg-white dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600 text-sm min-w-[100px]"
                    >
                        <span className="text-gray-700 dark:text-gray-300">{file.filename}</span>
                        <button
                            onClick={() => handleDelete(file.filename)}
                            disabled={deletingFiles.includes(file.filename)}
                            className={`p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 ${
                                deletingFiles.includes(file.filename)
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300'
                            }`}
                            title="Remove file"
                        >
                            {deletingFiles.includes(file.filename) ? (
                                <Loader size={14} className="animate-spin" />
                            ) : (
                                <X size={14} />
                            )}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};